import React, { FC } from 'react';

import { useIntl } from 'react-intl';

import { useNavigation } from 'hooks/navigation/use-navigation';
import { useFeatureLoyaltyRewardListCategoriesOnly } from 'hooks/use-feature-loyalty-reward-list-categories-only';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';

import { LoyaltyBannerSkeleton } from './loyalty-banner-skeleton';
import { PromoMessage } from './promo-message';
import {
  AuthenticatedContainer,
  DashboardButton,
  Line,
  LineProgress,
  Point,
  PointContainer,
  PointText,
  PointsContainer,
  StyledLoyaltyPointsIcon,
  TiersContainer,
  UserCrownPointsContainer,
  UserPoints,
} from './styled';
import { BannerTier } from './types';
import { getProgressBarValue, mapCategoriesToTiers, sanitizeStringToNumber } from './utils';

const AuthenticatedBanner: FC = () => {
  const {
    data: categories,
    loading: loadingCategories,
  } = useFeatureLoyaltyRewardListCategoriesOnly();
  const { navigate } = useNavigation();
  const { loyaltyUser } = useLoyaltyUser();
  const { formatMessage } = useIntl();

  const bestPromoId = loyaltyUser?.bestPromotion?.sanityId;
  const userPoints = loyaltyUser?.points || 0;

  const redeemClick = () => {
    navigate(formatMessage({ id: 'routes.loyaltyBannerAuthenticated' }));
  };

  if (loadingCategories || !categories?.length) {
    return <LoyaltyBannerSkeleton />;
  }

  const tiers: BannerTier[] = mapCategoriesToTiers(categories);

  const Tiers = ({ tiers }: { tiers: BannerTier[] }) => {
    return (
      <TiersContainer>
        <Line />
        <LineProgress percentCompleted={getProgressBarValue(userPoints, tiers)} />
        {tiers.map((tier, index) => {
          const isLast = index === tiers.length - 1;
          const tierText = tier.value;
          const completed = sanitizeStringToNumber(tierText) <= userPoints;
          const left = isLast ? '90%' : `${(index / (tiers.length - 1)) * 90}%`;
          return (
            <PointContainer key={index} left={left}>
              <Point completed={completed}>
                <PointText>{tier.value}</PointText>
              </Point>
            </PointContainer>
          );
        })}
      </TiersContainer>
    );
  };

  return (
    <AuthenticatedContainer testID="authenticated-banner" hasPromoMessage={!!bestPromoId}>
      {bestPromoId && <PromoMessage promoId={bestPromoId} />}
      <PointsContainer>
        <UserCrownPointsContainer>
          <StyledLoyaltyPointsIcon />
          <UserPoints testID="authenticated-banner-points">{userPoints}</UserPoints>
        </UserCrownPointsContainer>
        <DashboardButton onPress={redeemClick}>
          {formatMessage({ id: 'loyaltyBannerDashboardButtonLabel' })}
        </DashboardButton>
      </PointsContainer>
      <Tiers tiers={tiers} />
    </AuthenticatedContainer>
  );
};

export default AuthenticatedBanner;
